import { api, ApiError } from '@/shared/api/client';
import { useAuthStore, type AuthUser } from '@/shared/auth/store';
import * as idp from '@/shared/auth/idp';
import { readLastVault, rememberLastVault } from '@/shared/auth/last-env';
import type { PortalUserDto } from '@/shared/auth/idp';

/**
 * Auth against the central IdP (activation-service, `/auth/*`).
 *
 * The IdP verifies the password once, centrally, then replays each
 * environment's own credentials into `sp_Login_GET_UserInfo` and keeps the
 * resulting backend token server-side. See
 * plans/2026-08-29-portal-unified-shell.md, stage 1.
 *
 * KNOWN GAP: `/auth/login` has no confirmation-code step, so the users with
 * `dbo.Users.ConfirmationFrequency > 0` (18 across both production contours as
 * of 2026-08-29) cannot sign in through it. The legacy `master_onelogin` branch
 * has the same hole. Closing it means relaying the backend's `IsConfirmCode`
 * answer through the IdP; until then those accounts must keep using the old
 * login page.
 */

export interface LoginPayload {
  login: string;
  password: string;
  turnstileToken?: string;
}

/**
 * A message to show the user. `key` is looked up in the legacy `login`
 * dictionary; `fallback` is what renders while it loads or when the key does not
 * exist there — most IdP failures are new and have no legacy wording.
 */
export interface AuthMessage {
  key: string;
  fallback: string;
}

export type LoginOutcome =
  | { ok: true }
  /** Several contours available — the caller must show the picker. */
  | { ok: false; selectEnv: { ticket: string; envs: idp.IdpEnv[] } }
  | { ok: false; error: AuthMessage };

interface LoginResponseDto {
  Success: boolean;
  Error?: string;
  User?: PortalUserDto;
}

/**
 * Prefer `next`, but treat blank as absent.
 *
 * The environment's `/me` builds its answer from the backend token, where the
 * landing pages are empty strings rather than nulls (`AccessTokenObject`
 * initialises them so). A plain `??` therefore let `""` overwrite the page login
 * had already resolved, and the session lost its landing on the first reload.
 */
function keep(next: string | null | undefined, previous: string | null | undefined): string | null {
  return next?.trim() ? next : (previous ?? null);
}

/**
 * Map a session snapshot onto our user model.
 *
 * The environment's `/me` returns a narrower DTO than login (no
 * DefaultPageDistr, no OSMSettings, no UserSettings), so anything it omits is
 * carried over from `previous` instead of being dropped — otherwise every page
 * reload silently degraded the session.  Fields `/me` does own — DistrID,
 * ClientID, StorageDatabase, PortalAccess — always win, because they come from
 * the server-side token and change when the distributor is switched.
 */
function toAuthUser(dto: PortalUserDto, previous?: AuthUser | null): AuthUser {
  return {
    userId: dto.UserID,
    userGuid: dto.UserGUID,
    login: dto.Login,
    userName: dto.UserName,
    distrId: dto.DistrID,
    clientId: dto.ClientID,
    defaultPage: keep(dto.DefaultPage, previous?.defaultPage),
    defaultPageDistr: keep(dto.DefaultPageDistr, previous?.defaultPageDistr),
    defaultPageClient: keep(dto.DefaultPageClient, previous?.defaultPageClient),
    isTemporary: dto.IsTemporary ?? previous?.isTemporary,
    osmSettings: dto.OSMSettings ?? previous?.osmSettings ?? null,
    portalAccess: dto.PortalAccess ?? [],
    storageDatabase: dto.StorageDatabase ?? previous?.storageDatabase ?? null,
    distributors: dto.Distributors ?? previous?.distributors,
    userSettings: dto.UserSettings ?? previous?.userSettings,
  };
}

/**
 * Map an IdP error code onto something showable. Only the wrong-credentials
 * case has legacy wording (`WTK_LOGIN_COMMON_ERRORMESSAGE` — "Неверный логин или
 * пароль!"); the rest are conditions the legacy login could not produce, so they
 * carry their own text until someone adds keys for them.
 */
function idpMessage(code: string): AuthMessage {
  switch (code) {
    case 'invalid_credentials':
      return {
        key: 'WTK_LOGIN_COMMON_ERRORMESSAGE',
        fallback: 'Wrong login or password',
      };
    case 'captcha_failed':
      return { key: 'AUTH_CAPTCHA_FAILED', fallback: 'Security check failed, please retry' };
    case 'locked':
      return {
        key: 'AUTH_LOCKED',
        fallback: 'Too many failed attempts, try again in 15 minutes',
      };
    case 'no_access':
      return { key: 'AUTH_NO_ACCESS', fallback: 'This account has no environment access' };
    case 'backend_login_failed':
      return { key: 'AUTH_BACKEND_FAILED', fallback: 'The environment refused the login' };
    case 'ticket_expired':
      return {
        key: 'AUTH_TICKET_EXPIRED',
        fallback: 'The login attempt expired, please sign in again',
      };
    case 'unavailable':
      return { key: 'AUTH_UNAVAILABLE', fallback: 'Authentication service unavailable' };
    default:
      return { key: `AUTH_${code.toUpperCase()}`, fallback: 'Login failed' };
  }
}

function applySession(session: idp.SessionInfo | null, user: PortalUserDto | null) {
  const store = useAuthStore.getState();
  if (session) {
    store.setSession({
      login: session.login,
      vaultId: session.vaultId,
      envName: session.envName,
      role: session.role,
      envs: session.envs,
    });
  }
  if (user) store.setUser(toAuthUser(user, store.user));
}

export async function login(payload: LoginPayload): Promise<LoginOutcome> {
  try {
    const res = await idp.login(payload.login, payload.password, payload.turnstileToken);
    if (res.status !== 'select_env') {
      await adoptSession(res.user);
      return { ok: true };
    }

    // Several contours reachable. Go straight in instead of asking — the legacy
    // portal does the same, and the contour is changeable afterwards from the
    // nav bar, so a picker here would be an extra click on every single login
    // for the handful of accounts that have more than one grant.
    //
    // Where to go, in order: the contour this account entered last (the IdP
    // records it, so it follows the person between machines), then the one used
    // last on this machine, then the account's default. Otherwise someone whose
    // default is production is dropped there on every login however long they
    // have been working in test.
    const remembered = readLastVault(payload.login);
    const target =
      res.envs.find((e) => e.is_last) ??
      res.envs.find((e) => e.vault_id === remembered) ??
      res.envs.find((e) => e.is_default) ??
      res.envs[0];
    if (!target) return { ok: false, error: idpMessage('no_access') };

    const outcome = await selectEnv(res.ticket, target.vault_id);
    if (outcome.ok) return outcome;

    // The default contour refused us — most likely its backend is down. A
    // failed select-env leaves the ticket alive (only success deletes it), so
    // fall back to asking rather than stranding the user on one dead contour.
    return { ok: false, selectEnv: { ticket: res.ticket, envs: res.envs } };
  } catch (e) {
    if (e instanceof idp.IdpError) return { ok: false, error: idpMessage(e.code) };
    throw e;
  }
}

/** Second step of a multi-environment login. */
export async function selectEnv(ticket: string, vaultId: string): Promise<LoginOutcome> {
  try {
    const res = await idp.selectEnv(ticket, vaultId);
    await adoptSession(res.user);
    return { ok: true };
  } catch (e) {
    if (e instanceof idp.IdpError) return { ok: false, error: idpMessage(e.code) };
    throw e;
  }
}

/**
 * Move the whole session to another contour. The distributor picker is a
 * different thing — it stays inside one environment.
 */
export async function switchEnv(vaultId: string): Promise<void> {
  const res = await idp.switchEnv(vaultId);
  await adoptSession(res.user);
}

/**
 * Store the profile the IdP just handed us, then ask the IdP for the session
 * envelope (env list) so the switcher has something to render.
 */
async function adoptSession(user: PortalUserDto | null) {
  const session = await idp.me();
  applySession(session, user ?? session?.user ?? null);
  // Covers all three ways into a contour — login, the picker after a failed
  // default, and the nav-bar switch — since each of them ends up here.
  if (session) rememberLastVault(session.login, session.vaultId);
}

/**
 * Resolve the session on boot. No local state to validate any more: the cookie
 * either resolves or it does not.
 *
 * Two calls on purpose, and both profiles are used. `/auth/me` says whether a
 * session exists, which contours are reachable, and carries the full login
 * profile — including what the environment cannot report. It does go stale where
 * a distributor switch is concerned (that mutates only the backend token), so
 * the environment's own `/me` is layered on top of it rather than instead of it.
 */
export async function bootstrapSession(): Promise<void> {
  const { clear, setBootstrapped } = useAuthStore.getState();
  try {
    const session = await idp.me();
    if (!session) {
      clear();
      return;
    }

    // The IdP's copy goes in first so the environment's answer is merged onto it
    // rather than replacing it. It is written at login and is the only source of
    // the fields `/me` cannot report — above all the landing pages, which a
    // fresh tab has no local state to carry.
    applySession(session, session.user);

    try {
      const res = await api<LoginResponseDto>('/api/v3/auth/me');
      if (res.Success && res.User) applySession(null, res.User);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Cookie is valid but the environment rejected the injected token —
        // the session is unusable, so treat it as absent.
        clear();
        return;
      }
      // Network error: the IdP's cached profile stays as it is.
    }

    // The contour can also have been switched from the legacy portal, which
    // shares this cookie; the session we just read is the truth about it.
    rememberLastVault(session.login, session.vaultId);
  } catch {
    // IdP unreachable — stay anonymous rather than guessing.
    clear();
  } finally {
    setBootstrapped(true);
  }
}

/**
 * Re-read the profile from the environment. Used after the distributor is
 * switched: `SP_Common/setDefaultDistr` mutates DistrID / ClientID /
 * StorageDatabase inside the backend token, and its `/me` is the only thing
 * that reports what actually ended up there.
 */
export async function refreshSession(): Promise<void> {
  const res = await api<LoginResponseDto>('/api/v3/auth/me');
  if (!res.Success || !res.User) throw new ApiError(401, res.Error ?? 'Session refresh failed');
  useAuthStore.getState().setUser(toAuthUser(res.User, useAuthStore.getState().user));
}

/**
 * Local state goes first. Route guards read the store synchronously, so a caller
 * that navigates to /login without awaiting this would still look authenticated
 * to the `_public` guard and get bounced back to the app.
 */
export async function logout(): Promise<void> {
  useAuthStore.getState().clear();
  await idp.logout();
}
